import React, { useState } from 'react';
import {
  Paper,
  FormControlLabel,
  Grid,
  Checkbox,
  Typography,
  Box,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from '../../../../hooks/useTranslation';
import Modal from '../../../modal';
import Button from '../../../buttons';
import colors from '../../../../themes/main-colors';
import { Meter } from '../../../../components/ssm/ssm.types';
import { useIsMobile } from '../../../../util/style-utils';
import TextLink from '../../../text-link';
import theme from '../../../../themes/theme';

type MultipleMetersProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (selectedMeters: string[]) => void;
  userAddress: string;
  meters: Meter[];
  selectedMeters: string[];
  setSelectedMeters: React.Dispatch<React.SetStateAction<string[]>>;
};

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: '32px',
      color: colors.noirBlur,
      marginBottom: '6px',
    },
    ineligibleModalContainer: {
      maxWidth: '680px',
    },
    eligibleModalContainer: {
      maxWidth: '800px',
      [theme.breakpoints.down('sm')]: {
        maxHeight: '80vh',
        overflowY: 'auto',
      },
    },
    descriptionText: {
      fontSize: '20px',
      color: colors.noirBlur,
      [theme.breakpoints.down('sm')]: {
        fontSize: '18px',
      },
    },
    ineligibleModalBody: {
      backgroundColor: colors.modalBackgroundColor,
      marginTop: '20px',
      padding: '10px 25px 10px 25px',
      borderRadius: '4px',
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
    },
    bodyText: {
      fontSize: '18px',
      color: colors.noirBlur,
      marginTop: '10px',
    },
    ineligibleModalActions: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '20px',
    },
    ineligibleModalClose: {
      fontSize: '18px',
      color: colors.white,
      padding: '5px 25px 5px 25px',
    },
    marginBottom: {
      marginBottom: '6px',
    },
    eligibleModalClose: {
      fontSize: '18px',
      padding: '5px 25px 5px 25px',
      [theme.breakpoints.down('sm')]: {
        width: '45%',
      },
    },
    eligibleModalSelect: {
      fontSize: '18px',
      color: colors.white,
      padding: '5px 25px 5px 25px',
      [theme.breakpoints.down('sm')]: {
        width: '45%',
        marginLeft: '10px',
      },
    },
    eligibleModalActions: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px',
    },
    selectAllMeters: {
      color: colors.lightGray5,
      marginLeft: '15px',
      marginBottom: '10px',
    },
    metersList: {
      display: 'flex',
      flexWrap: 'wrap',
      marginBottom: '6px',
      maxHeight: '250px',
      overflowY: 'auto',
      scrollbarColor: `${colors.white} ${colors.scrollbarBackground}`,
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': {
        width: '10px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: colors.white,
        borderRadius: '10px',
      },
      '&::-webkit-scrollbar-track': {
        background: colors.scrollbarBackground,
        borderRadius: '10px',
      },
      [theme.breakpoints.down('sm')]: {
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          width: 0,
        },
      },
    },
    mobileMarginBottom: {
      [theme.breakpoints.down('sm')]: {
        marginBottom: '15px',
      },
    },
    meter: {
      display: 'flex',
      maxWidth: '49%',
      flex: '40%',
      marginRight: '10px',
      marginBottom: '10px',
      padding: '10px 15px 10px 0px',
      borderRadius: '4px',
      [theme.breakpoints.down('sm')]: {
        maxWidth: 'unset',
        width: '100%',
        flex: 'unset',
      },
    },
    meterBody: {
      width: '100%',
      height: '100%',
      wordBreak: 'break-word',
      color: colors.lightGray5,
    },
    eligibleModalBody: {
      backgroundColor: colors.modalBackgroundColor,
      marginTop: '20px',
      padding: '10px 5px 10px 15px',
      borderRadius: '4px',
      boxShadow: `0px 3px 6px ${colors.boxShadowColor}`,
    },
    meterCheckbox: {
      marginTop: '-10px',
    },
    textColor: {
      color: colors.noirBlur,
    },
    meterDetails: {
      fontSize: '16px',
    },
    callPGEButton: {
      fontSize: '18px',
      padding: '10px 25px 10px 25px',
      backgroundColor: colors.orange,
      borderColor: colors.orange,
      marginTop: '10px',
      borderRadius: '4px',
      '& > a': {
        textDecoration: 'none',
        color: colors.white,
      },
      '& > a:hover': {
        textDecoration: 'none',
      },
      '& > a:active': {
        textDecoration: 'none',
      },
    },
  }),
);

export const MultipleMeters = (props: MultipleMetersProps) => {
  const {
    isOpen,
    handleClose,
    handleSubmit,
    userAddress,
    meters,
    selectedMeters,
    setSelectedMeters,
  } = props;
  const { t, inlineRichT } = useTranslation();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const hasMoreThanTenMeters = meters.length > 10 ? true : false;
  const numberOfEligibleMeters = meters.filter(
    (meter: Meter) => !meter.disabled,
  ).length;

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked === true) {
      setSelectedMeters([...selectedMeters, e.target.value]);
    } else if (e.target.checked === false) {
      const _selectedMeters = selectedMeters.filter(
        spId => spId !== e.target.value,
      );
      setSelectedMeters(_selectedMeters);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked === true) {
      const spIds = meters.map(meter => {
        if (!meter.disabled) {
          return meter.spId;
        } else {
          return '';
        }
      });
      setSelectedMeters(spIds.filter(Boolean));
    } else if (e.target.checked === false) {
      setSelectedMeters([]);
    }
  };

  const handleModalCancel = () => {
    handleClose();
    setSelectedMeters([]);
  };

  const handleModalSubmit = () => {
    handleSubmit(selectedMeters);
    setSelectedMeters([]);
  };

  return (
    <Modal
      open={isOpen}
      showCloseIcon={true}
      onClose={handleModalCancel}
      title={
        <Typography
          variant="body1"
          className={classes.title}
          data-testid="modal-title"
        >
          {hasMoreThanTenMeters
            ? t('MULTIPLE_METERS_FOUND')
            : t('SELECT_METERS')}
        </Typography>
      }
    >
      {hasMoreThanTenMeters ? (
        <Grid container className={classes.ineligibleModalContainer}>
          <Grid container>
            <Typography
              className={classes.descriptionText}
              data-testid="modal-description-text"
            >
              {t('SEVERAL_METERS_ASSOCIATED_WITH_THIS_ADDRESS')}
            </Typography>
          </Grid>
          <Grid container className={classes.ineligibleModalBody}>
            {isMobile ? (
              <>
                <Typography
                  className={classes.bodyText}
                  data-testid="modal-body-text-mobile-1"
                >
                  {inlineRichT(
                    'MULTIPLE_METERS_FOUND_MODAL_BODY_TEXT_MOBILE_1',
                    {
                      numberOfMeters: String(meters.length),
                      userAddress,
                    },
                  )}
                </Typography>
                <Typography
                  className={`${classes.bodyText} ${classes.marginBottom}`}
                  data-testid="modal-body-text-mobile-2"
                >
                  {inlineRichT(
                    'MULTIPLE_METERS_FOUND_MODAL_BODY_TEXT_MOBILE_2',
                    {
                      RESIDENTIAL_CUSTOMER_SERVICE_NUMBER: t(
                        'RESIDENTIAL_CUSTOMER_SERVICE_NUMBER',
                      ),
                    },
                  )}
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  className={classes.bodyText}
                  data-testid="modal-body-text-1"
                >
                  {inlineRichT('MULTIPLE_METERS_FOUND_MODAL_BODY_TEXT_1', {
                    numberOfMeters: String(meters.length),
                    userAddress,
                  })}
                </Typography>
                <Typography
                  className={`${classes.bodyText} ${classes.marginBottom}`}
                  data-testid="modal-body-text-2"
                >
                  {inlineRichT('MULTIPLE_METERS_FOUND_MODAL_BODY_TEXT_2', {
                    RESIDENTIAL_CUSTOMER_SERVICE_NUMBER: t(
                      'RESIDENTIAL_CUSTOMER_SERVICE_NUMBER',
                    ),
                  })}
                </Typography>
              </>
            )}
          </Grid>
          {isMobile ? (
            <Grid container className={classes.ineligibleModalActions}>
              <Box
                className={classes.callPGEButton}
                data-testid="modal-mobile-call-pge"
              >
                <TextLink
                  external
                  to={`tel:${t('RESIDENTIAL_CUSTOMER_SERVICE_NUMBER')}`}
                >
                  {t('CALL_PGE_CUSTOMER_SERVICE')}
                </TextLink>
              </Box>
            </Grid>
          ) : null}
          <Grid container className={classes.ineligibleModalActions}>
            <Button
              color={'primary'}
              variant={'contained'}
              className={classes.ineligibleModalClose}
              onClick={handleClose}
              data-testid="modal-close-button"
            >
              {t('CLOSE')}
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Grid container className={classes.eligibleModalContainer}>
          <Grid container className={classes.marginBottom}>
            <Typography
              className={`${classes.descriptionText} ${classes.mobileMarginBottom}`}
              data-testid="modal-description-text-1"
            >
              {inlineRichT('SEVERAL_ASSOCIATED_METERS_FOUND', {
                userAddress,
              })}
            </Typography>
            <Typography
              className={`${classes.descriptionText} ${classes.marginBottom} ${classes.mobileMarginBottom}`}
              data-testid="modal-description-text-2"
            >
              {t('SELECT_METER_TO_START_SERVICE_FOR')}
            </Typography>
            <Typography
              className={classes.descriptionText}
              data-testid="modal-description-text-3"
            >
              {inlineRichT('UNABLE_TO_START_METER_IN_MODAL', {
                PGE_PHONE: t('PGE_PHONE'),
              })}
            </Typography>
          </Grid>
          <Grid container className={classes.eligibleModalBody}>
            <Box className={classes.selectAllMeters}>
              <FormControlLabel
                control={
                  <Checkbox
                    name={'selectAllMeters'}
                    color={'primary'}
                    checked={numberOfEligibleMeters === selectedMeters.length}
                    onChange={handleSelectAll}
                    data-testid="modal-select-all"
                  />
                }
                label={t('SELECT_ALL')}
              />
            </Box>
            <Grid container className={classes.metersList}>
              {meters.map(meter => (
                <Paper className={classes.meter} key={meter.spId}>
                  <Box className={classes.meterCheckbox}>
                    <Checkbox
                      name={meter.spId}
                      color={'primary'}
                      value={meter.spId}
                      disabled={meter.disabled}
                      checked={selectedMeters.includes(meter.spId)}
                      onChange={handleSelect}
                      data-testid="modal-meter"
                    />
                  </Box>
                  <Box className={classes.meterBody}>
                    <Typography
                      className={classes.meterDetails}
                      data-testid="modal-meter-type"
                    >
                      {t('TYPE')}
                      {': '}
                      <b className={classes.textColor}>{meter.spDesc}</b>
                    </Typography>
                    {meter.locationDesc ? (
                      <Typography
                        className={classes.meterDetails}
                        data-testid="modal-meter-location"
                      >
                        {t('LOCATION')}
                        {': '}
                        <b className={classes.textColor}>
                          {meter.locationDesc}
                        </b>
                      </Typography>
                    ) : meter.meterNumber ? (
                      <Typography
                        className={classes.meterDetails}
                        data-testid="modal-meter-number"
                      >
                        {t('METER_NO')}
                        {': '}
                        <b className={classes.textColor}>{meter.meterNumber}</b>
                      </Typography>
                    ) : null}
                    {meter.disabled ? (
                      <Typography className={classes.meterDetails}>
                        {t('METER_IS_COMMERCIAL_ERROR')}
                      </Typography>
                    ) : null}
                  </Box>
                </Paper>
              ))}
            </Grid>
          </Grid>
          <Grid container className={classes.eligibleModalActions}>
            <Button
              color={'secondary'}
              variant={'outlined'}
              className={classes.eligibleModalClose}
              onClick={handleModalCancel}
              data-testid="modal-close-button"
            >
              {t('CANCEL')}
            </Button>
            <Button
              color={'primary'}
              variant={'contained'}
              className={classes.eligibleModalSelect}
              onClick={handleModalSubmit}
              data-testid="modal-select-button"
              disabled={!Boolean(selectedMeters?.length)}
            >
              {t('SELECT')}
            </Button>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};
